// reduxjs
import { createSlice, Dispatch } from '@reduxjs/toolkit';
// utils
import { DateTime } from 'luxon';
// types
import {
  GridAggregationModel,
  GridColumnVisibilityModel,
  GridRowGroupingModel,
  GridSortModel,
  GridFilterModel,
} from '@mui/x-data-grid-premium';
import { IReportTemplateTableFilters } from 'src/types/report-template';
// api
import {
  ReportsService,
  ReportTableDto,
  ReportRowDto,
  ReportTemplateDto,
  ReportColumnDto,
  ReportPinnedModelDto,
} from '../../api';
// error
import { setError } from './error';
// columns
import { ColumnTemplates } from '../../components/report/column';

// ----------------------------------------------------------------------

const initialState = {
  loading: { 0: false } as { [index: number]: boolean },
  reports: [] as ReportTemplateDto[],
  report: {} as ReportTemplateDto,
  template: {} as ReportTemplateDto,
  rows: [{ id: '' }] as ReportRowDto[],
  intervalRows: [] as { [interval: number]: ReportRowDto[] },
  filter: {
    fromDate: DateTime.now().startOf('month'),
    toDate: DateTime.now().endOf('month'),
    dataSource: [],
  } as IReportTemplateTableFilters,
};

const slice = createSlice({
  name: 'report',
  initialState,
  reducers: {
    setLoading(state, action) {
      if (state.loading[action.payload.index] !== action.payload.state) {
        state.loading[action.payload.index] = action.payload.state;
      }
    },
    setReports(state, action) {
      state.reports = action.payload;
    },
    setReport(state, action) {
      state.report = action.payload;
    },
    setTemplate(state, action) {
      state.template = action.payload;
    },
    setReportRows(state, action) {
      state.rows = action.payload;
    },
    setIntervalRows(state, action) {
      state.intervalRows[action.payload.interval] = action.payload.rows;
    },
    addReportTabel(state, action) {
      state.report.tables!.push(action.payload.table);
    },
    removeReportTabel(state, action) {
      state.report.tables!.splice(action.payload.index, 1);
    },
    updateColumns(state, action) {
      state.report.tables![action.payload.index].columns = action.payload.columns;
    },
    setColumnVisibilityModel(state, action) {
      state.report.tables![action.payload.index].columnVisibilityModel =
        action.payload.columnVisibilityModel;
    },
    setRowGroupingModel(state, action) {
      state.report.tables![action.payload.index].rowGroupingModel = action.payload.rowGroupingModel;
    },
    setSortModel(state, action) {
      state.report.tables![action.payload.index].sortModel = action.payload.sortModel;
    },
    setFilterModel(state, action) {
      state.report.tables![action.payload.index].filterModel = action.payload.filterModel;
    },
    updateFilterModelItemValue(state, action) {
      state.report.tables![action.payload.tableIndex].filterModel!.items![
        action.payload.itemIndex
      ].value = action.payload.value;
    },
    updateFilterModelItemTemplate(state, action) {
      state.report.tables![action.payload.tableIndex].filterModel!.items![
        action.payload.itemIndex
      ].template = action.payload.template;
    },
    setAggregationModel(state, action) {
      state.report.tables![action.payload.index].aggregationModel = action.payload.aggregationModel;
    },
    setCustomWidth(state, action) {
      state.report.tables![action.payload.index].columns![action.payload.columnIndex].width =
        action.payload.width;
    },
    setDataSource(state, action) {
      state.report.dataSource = action.payload.dataSource;
    },
    setPageSize(state, action) {
      state.report.tables![action.payload.index].pageSize = action.payload.pageSize;
    },
    setIntervalModel(state, action) {
      state.report.tables![action.payload.index].intervalModel = action.payload.intervalModel;
    },
    setDescription(state, action) {
      state.report.tables![action.payload.index].description = action.payload.description;
    },
    setFilter(state, action) {
      state.filter[action.payload.key as keyof IReportTemplateTableFilters] = action.payload.filter;
    },
    setPinnedModel(state, action) {
      state.report.tables![action.payload.index].pinnedModel = action.payload.pinnedModel;
    },
  },
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function setLoading(state: boolean, index: number) {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.setLoading({ state, index }));
  };
}

export function getReports() {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.setLoading({ state: true, index: 0 }));
    const response = await ReportsService.getAllReportTemplates();
    dispatch(slice.actions.setReports(response));
    dispatch(slice.actions.setLoading({ state: false, index: 0 }));
  };
}

export function getReport(
  params: {
    templateId?: string;
    global?: boolean;
  } = {} as any
) {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.setReport([]));
    dispatch(slice.actions.setReportRows([{ id: '' }]));
    if (params.templateId) {
      if (params.templateId.includes('global')) {
        params.templateId = params.templateId.replace('-global', '');
        params.global = true;
      }
      try {
        const response = await ReportsService.getReportTemplate({
          id: Number(params.templateId),
          global: params.global ?? false,
        });

        if (response && response.tables) {
          response.tables.forEach((x) => {
            if (!x.columns || x.columns.length === 0) {
              x.columns = [...ColumnTemplates().sort((a, b) => a.index! - b.index!)];
            }
            if (x.filterModel && x.filterModel.items) {
              x.filterModel.items.forEach((y: any) => {
                try {
                  if (y.value && (y.value as DateTime)) {
                    y.value = `${y.value.toFormat('yyyy-LL-dd')}T${y.value.toFormat(
                      'HH:mm'
                    )}${y.value.toFormat('ZZ')}`;
                  }
                } catch (error) {
                  // No error handling needed, as this is used as a try parse statement.
                }
              });
            }
          });
        }

        if (response.tables) {
          dispatch(slice.actions.setReport(response));
          dispatch(slice.actions.setFilter({ key: 'dataSource', filter: response.dataSource }));
          if (response.dataSource!.length === 0) {
            dispatch(slice.actions.setReportRows([]));
          }
        }
      } catch (error) {
        dispatch(setError(error));
      }
    } else {
      const response = await ReportsService.getTemplate();

      if (response.tables![0] && !response.tables![0].columns) {
        const report = {
          ...response,
          tables: [
            {
              ...response.tables![0],
              columns: [...ColumnTemplates().sort((a, b) => a.index! - b.index!)],
            },
          ],
        };

        dispatch(slice.actions.setReport(report));
        dispatch(slice.actions.setTemplate(report));
        dispatch(slice.actions.setFilter({ key: 'dataSource', filter: report.dataSource }));
      } else {
        dispatch(slice.actions.setReport(response));
        dispatch(slice.actions.setTemplate(response));
        dispatch(slice.actions.setFilter({ key: 'dataSource', filter: response.dataSource }));
      }
    }
  };
}

export function getTemplate() {
  return async (dispatch: Dispatch) => {
    const response = await ReportsService.getTemplate();

    dispatch(
      slice.actions.setTemplate({
        ...response,
        tables: [
          {
            ...response.tables![0],
            columns: [...ColumnTemplates().sort((a, b) => a.index! - b.index!)],
          },
        ],
      })
    );
    dispatch(slice.actions.setFilter({ key: 'dataSource', filter: response.dataSource }));
  };
}

export function deleteReportTemplate(
  params: {
    templateId: string;
    global?: boolean;
  } = {} as any
) {
  return async (dispatch: Dispatch) => {
    if (params.templateId) {
      if (params.templateId.includes('global')) {
        params.templateId = params.templateId.replace('-global', '');
        params.global = true;
      }
      try {
        dispatch(slice.actions.setLoading({ state: true, index: 0 }));
        await ReportsService.deleteReportTemplate({
          id: Number(params.templateId),
        });
        dispatch(slice.actions.setLoading({ state: false, index: 0 }));
      } catch (error) {
        dispatch(setError(error));
      }
    }
  };
}

export function getReportRows(
  params: {
    pageSize?: number | null;
    pageNumber?: number | null;
    templateId: string | null;
    fromDate: DateTime | null;
    toDate: DateTime;
    dataSource: string[] | null;
    interval?: number;
  } = {} as any
) {
  return async (dispatch: Dispatch) => {
    try {
      if (params.interval) {
        dispatch(slice.actions.setLoading({ state: true, index: params.interval }));
        params.fromDate = params.toDate.plus({ months: -params.interval });
      } else {
        dispatch(slice.actions.setLoading({ state: true, index: 0 }));
      }

      const response = await ReportsService.getRows(params as any);

      response.map((row: ReportRowDto) =>
        Object.keys(row).forEach((key) => {
          const value: any = row[key as keyof ReportRowDto];

          if (value.isLuxonDateTime || value instanceof DateTime) {
            Object.assign(row, { [key as keyof ReportRowDto]: value.toJSDate() });
          }
        })
      );

      if (response && response.length > 0) {
        if (params.interval) {
          dispatch(slice.actions.setIntervalRows({ rows: response, interval: params.interval }));
          dispatch(slice.actions.setLoading({ state: false, index: params.interval }));
        } else {
          dispatch(slice.actions.setReportRows(response));
          dispatch(slice.actions.setLoading({ state: false, index: 0 }));
        }
      } else {
        dispatch(slice.actions.setReportRows([]));
        dispatch(slice.actions.setLoading({ state: false, index: 0 }));
      }
    } catch (error) {
      dispatch(setError(error));
      dispatch(slice.actions.setLoading({ state: false, index: params.interval ?? 0 }));
    }
  };
}

export function resetReportRows(template?: any) {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.setReportRows(template ?? {}));
  };
}

export function addReportTabel(
  params: {
    table: ReportTableDto;
  } = {} as any
) {
  return (dispatch: Dispatch) => {
    dispatch(slice.actions.addReportTabel({ table: params.table }));
  };
}

export function removeReportTabel(
  params: {
    index: number;
  } = {} as any
) {
  return (dispatch: Dispatch) => {
    dispatch(slice.actions.removeReportTabel({ index: params.index }));
  };
}

export function updateColumns(
  params: { columns: ReportColumnDto<any, any, any>[]; index: number } = {} as any
) {
  return (dispatch: Dispatch) => {
    dispatch(slice.actions.updateColumns({ columns: params.columns, index: params.index }));
  };
}

export function setColumnVisibilityModel(
  params: { columnVisibilityModel: GridColumnVisibilityModel; index: number } = {} as any
) {
  return (dispatch: Dispatch) => {
    dispatch(
      slice.actions.setColumnVisibilityModel({
        columnVisibilityModel: params.columnVisibilityModel,
        index: params.index,
      })
    );
  };
}

export function setRowGroupingModel(
  params: { rowGroupingModel: GridRowGroupingModel; index: number } = {} as any
) {
  return (dispatch: Dispatch) => {
    dispatch(
      slice.actions.setRowGroupingModel({
        rowGroupingModel: params.rowGroupingModel,
        index: params.index,
      })
    );
  };
}

export function setSortModel(params: { sortModel: GridSortModel; index: number } = {} as any) {
  return (dispatch: Dispatch) => {
    dispatch(
      slice.actions.setSortModel({
        sortModel: params.sortModel,
        index: params.index,
      })
    );
  };
}

export function setFilterModel(
  params: { filterModel: GridFilterModel; index: number; baseValueOptions?: string } = {} as any
) {
  return (dispatch: Dispatch) => {
    dispatch(
      slice.actions.setFilterModel({
        filterModel: {
          ...params.filterModel,
          items: [
            ...params.filterModel.items.map((x) =>
              typeof x.value === 'number' || typeof x.value === 'boolean'
                ? { ...x, value: x.value.toString() }
                : { ...x, value: x.value }
            ),
          ],
        },
        index: params.index,
      })
    );
  };
}

export function setAggregationModel(
  params: { aggregationModel: GridAggregationModel; index: number } = {} as any
) {
  return (dispatch: Dispatch) => {
    dispatch(
      slice.actions.setAggregationModel({
        aggregationModel: params.aggregationModel,
        index: params.index,
      })
    );
  };
}

export function setCustomWidth(
  params: { columnIndex: number; width: number; index: number } = {} as any
) {
  return (dispatch: Dispatch) => {
    dispatch(
      slice.actions.setCustomWidth({
        columnIndex: params.columnIndex,
        width: params.width,
        index: params.index,
      })
    );
  };
}

export function setDataSource(params: { dataSource: string[] } = {} as any) {
  return (dispatch: Dispatch) => {
    dispatch(
      slice.actions.setDataSource({
        dataSource: params.dataSource,
      })
    );
  };
}

export function setPageSize(params: { pageSize: number; index: number } = {} as any) {
  return (dispatch: Dispatch) => {
    dispatch(
      slice.actions.setPageSize({
        pageSize: params.pageSize,
        index: params.index,
      })
    );
  };
}

export function setIntervalModel(params: { intervalModel: number[]; index: number } = {} as any) {
  return (dispatch: Dispatch) => {
    dispatch(
      slice.actions.setIntervalModel({
        intervalModel: params.intervalModel,
        index: params.index,
      })
    );
  };
}

export function setDescription(params: { description: string; index: number } = {} as any) {
  return (dispatch: Dispatch) => {
    dispatch(
      slice.actions.setDescription({
        description: params.description,
        index: params.index,
      })
    );
  };
}

export function updateFilterModelItemValue(
  params: { tableIndex: number; itemIndex: number; value: any } = {} as any
) {
  return (dispatch: Dispatch) => {
    dispatch(
      slice.actions.updateFilterModelItemValue({
        tableIndex: params.tableIndex,
        itemIndex: params.itemIndex,
        value: params.value,
      })
    );
  };
}

export function updateFilterModelItemTemplate(
  params: { tableIndex: number; itemIndex: number; template: string } = {} as any
) {
  return (dispatch: Dispatch) => {
    dispatch(
      slice.actions.updateFilterModelItemTemplate({
        tableIndex: params.tableIndex,
        itemIndex: params.itemIndex,
        template: params.template,
      })
    );
  };
}

export function setFilter(params: { key: string; filter: any } = {} as any) {
  return (dispatch: Dispatch) => {
    dispatch(slice.actions.setFilter({ key: params.key, filter: params.filter }));
  };
}

export function setPinnedModel(params: { pinnedModel: ReportPinnedModelDto; index: number }) {
  return (dispatch: Dispatch) => {
    dispatch(
      slice.actions.setPinnedModel({ pinnedModel: params.pinnedModel, index: params.index })
    );
  };
}
